import { useState, type FormEvent } from "react";
import { contact } from "@/mocks/gossip";

const FORM_URL = "https://readdy.ai/api/form/daf7kihtto0ceqqnh760";

type Status = "idle" | "loading" | "success" | "error";

export default function Newsletter() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 反垃圾：honeypot 命中则伪装成功，不提交
    const trap = String(formData.get("company_alt") || "").trim();
    if (trap) {
      setStatus("success");
      setFormError("");
      return;
    }
    formData.delete("company_alt");

    setStatus("loading");
    setFormError("");
    try {
      const body = new URLSearchParams(formData as unknown as URLSearchParams).toString();
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMsg).includes("spam") || String(responseText).includes("form data is spam");

      if (res.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "系统检测到疑似异常提交，请稍后再试。"
            : String(serverMsg || "提交失败，请检查内容后重试。")
        );
      }
    } catch (err) {
      setStatus("error");
      setFormError("网络开小差了，请稍后重试。");
    }
  };

  return (
    <section
      id="newsletter"
      className="relative scroll-mt-24 overflow-hidden bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800 py-10 md:py-16"
    >
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-8 px-4 md:px-6 lg:grid-cols-2">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[12px] font-semibold tracking-wide text-white backdrop-blur">
            <i className="ri-megaphone-line" />
            线索征集 · 每日速递订阅
          </span>
          <h2 className="mt-4 font-heading text-2xl font-black leading-snug text-white md:text-[34px]">
            你手里的一手料，可能就是下一个全网首发
          </h2>
          <p className="mt-3 max-w-lg text-[14px] leading-relaxed text-white/85 md:text-[15px]">
            想投稿爆料、订阅每日吃瓜速递，或是有内容建议？留下线索与邮箱，前线编辑 48 小时内与你沟通。
            线索一经核实采纳，我们会在署名与稿酬上给你应得的尊重。
          </p>
          <div className="mt-5 flex flex-col gap-2.5 text-[13px] text-white/80 sm:flex-row sm:items-center sm:gap-6">
            <span className="inline-flex items-center gap-2">
              <i className="ri-mail-line text-gold-300" /> {contact.email}
            </span>
            <span className="inline-flex items-center gap-2">
              <i className="ri-shield-star-line text-gold-300" /> 线索来源绝对保密
            </span>
            <span className="inline-flex items-center gap-2">
              <i className="ri-time-line text-gold-300" /> 24 小时内响应
            </span>
          </div>
        </div>

        <form
          className="rounded-2xl bg-white p-5 md:p-7"
          onSubmit={submit}
          data-readdy-form
          aria-label="吃瓜线索征集与订阅表单"
        >
          <p className="font-heading text-[18px] font-bold text-[#241b13]">投一手料 / 订阅速递</p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col">
              <label className="mb-1 text-[12px] font-medium text-stone-500" htmlFor="nl-name">
                怎么称呼你
              </label>
              <input
                id="nl-name"
                name="name"
                type="text"
                placeholder="例：热心猹友"
                className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 text-[14px] text-stone-800 outline-none transition-colors focus:border-brand-400 focus:bg-white"
              />
            </div>
            <div className="flex flex-col">
              <label className="mb-1 text-[12px] font-medium text-stone-500" htmlFor="nl-topic">
                你想投的类别
              </label>
              <select
                id="nl-topic"
                name="topic"
                defaultValue="独家线索"
                className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 text-[14px] text-stone-800 outline-none focus:border-brand-400 focus:bg-white"
              >
                <option>独家线索</option>
                <option>投稿爆料</option>
                <option>内容纠错</option>
                <option>合作洽谈</option>
                <option>其他建议</option>
              </select>
            </div>
          </div>
          <div className="mt-3 flex flex-col">
            <label className="mb-1 text-[12px] font-medium text-stone-500" htmlFor="nl-email">
              接收速递的邮箱 <span className="text-brand-600">*</span>
            </label>
            <input
              id="nl-email"
              name="email"
              type="email"
              required
              placeholder="you@example.com"
              className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 text-[14px] text-stone-800 outline-none transition-colors focus:border-brand-400 focus:bg-white"
            />
          </div>
          <div className="mt-3 flex flex-col">
            <label className="mb-1 text-[12px] font-medium text-stone-500" htmlFor="nl-message">
              线索描述 / 留言（选填，最多 500 字）
            </label>
            <textarea
              id="nl-message"
              name="message"
              rows={3}
              maxLength={500}
              placeholder="例如：某节目录制现场信息、可以核实的时间与来源……"
              className="resize-none rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 text-[14px] leading-relaxed text-stone-800 outline-none transition-colors focus:border-brand-400 focus:bg-white"
            />
            <span className="mt-1 text-right text-[11px] text-stone-400">0 / 500</span>
          </div>

          {/* 反垃圾字段 */}
          <input
            className="formtrap"
            type="text"
            name="company_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          <button
            type="submit"
            disabled={status === "loading"}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-brand-500 px-4 py-3 text-[15px] font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {status === "loading" ? (
              <>
                <i className="ri-loader-4-line animate-spin" /> 提交中…
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill" /> 提交线索 · 订阅速递
              </>
            )}
          </button>

          {status === "success" && (
            <p className="mt-3 flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2.5 text-[13px] font-medium text-green-700">
              <i className="ri-checkbox-circle-fill" /> 收到啦！前线编辑会尽快联系你，记得留意邮箱。
            </p>
          )}
          {status === "error" && (
            <p className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2.5 text-[13px] font-medium text-red-600">
              <i className="ri-error-warning-fill" /> {formError}
            </p>
          )}
        </form>
      </div>
    </section>
  );
}